import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Briefcase,
  ChevronDown,
  Home,
  type LucideIcon,
  MoreHorizontal,
  Plus,
  RefreshCw,
  Repeat,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { PassCreateDialog, type PassCreateType } from '@/components/resident/PassCreateDialog';
import { PassDetailSheet, type PassDetailSelection } from '@/components/resident/PassDetailSheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { ResponsiveTable, type ResponsiveColumn } from '@/components/ui/responsive-table';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/contexts/AuthContext';
import { useResidentProperty } from '@/hooks/useResidentProperty';
import { useTranslation } from '@/hooks/useTranslation';
import {
  getDomesticWorkers,
  getRecurringPasses,
  reinstateRecurringPass,
  renewDomesticWorker,
  renewRecurringPass,
  revokeDomesticWorker,
  revokeRecurringPass,
  suspendDomesticWorker,
  suspendRecurringPass,
} from '@/services/api.client';
import type { RecurringPass } from '@/types';
import { cn, formatDate, statusBadgeClass } from '@/lib/utils';

const DAY_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// A pass within this many days of its end date is surfaced as "expiring soon"
// per RES-F-09 (amber highlight at the top of the Active group).
const EXPIRING_SOON_DAYS = 7;

function formatSchedule(pass: RecurringPass): string {
  const days = pass.scheduleDays?.map((d) => DAY_ABBR[d]).join(', ') ?? 'Daily';
  return `${days} · ${pass.entryTimeStart}–${pass.entryTimeEnd}`;
}

// Pass *type* is metadata, not a lifecycle status — it must not borrow the
// fixed status colour palette. Each type gets a neutral outline badge + icon.
const PASS_TYPE_META: Record<RecurringPass['passType'], { labelKey: string; Icon: LucideIcon }> = {
  recurring: { labelKey: 'resident.passes.typeRecurring', Icon: Repeat },
  domestic_worker: { labelKey: 'resident.passes.typeDomestic', Icon: Home },
  contractor: { labelKey: 'resident.passes.typeContractor', Icon: Briefcase },
};

function daysUntil(iso: string): number {
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000);
}

/** Active passes whose end date falls within the next EXPIRING_SOON_DAYS. */
function isExpiringSoon(pass: RecurringPass): boolean {
  if (pass.status !== 'active' || !pass.validUntil) return false;
  const d = daysUntil(pass.validUntil);
  return d >= 0 && d <= EXPIRING_SOON_DAYS;
}

function PassTypeBadge({ passType }: { passType: RecurringPass['passType'] }) {
  const { t } = useTranslation();
  const { labelKey, Icon } = PASS_TYPE_META[passType];
  return (
    <span className="inline-flex items-center gap-1 rounded-md border border-[var(--border-subtle)] px-2 py-0.5 text-xs font-medium text-[var(--text-secondary)]">
      <Icon className="size-3" aria-hidden="true" />
      {t(labelKey)}
    </span>
  );
}

// ── Renew dialog ─────────────────────────────────────────────────────────────

/** YYYY-MM-DD for a Date — used as the min/default for the date input. */
function toDateInput(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function PassRenewDialog({
  open,
  onOpenChange,
  pass,
  propertyId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pass: RecurringPass;
  propertyId: string;
}) {
  const qc = useQueryClient();
  const isDomestic = pass.passType === 'domestic_worker';
  const queryKey = isDomestic ? ['domestic-workers', propertyId] : ['recurring-passes', propertyId];

  // A renewal can only push the end date forward — never earlier than the
  // current end date (or today, whichever is later).
  const minDate = (() => {
    const today = new Date();
    const current = pass.validUntil ? new Date(pass.validUntil) : today;
    return toDateInput(current > today ? current : today);
  })();

  const [value, setValue] = useState(minDate);
  const [error, setError] = useState<string | null>(null);

  // Reset the picker whenever the dialog re-opens for a (possibly different) pass.
  useEffect(() => {
    if (open) {
      setValue(minDate);
      setError(null);
    }
  }, [open, minDate]);

  const renew = useMutation({
    mutationFn: (validUntil: string) =>
      isDomestic
        ? renewDomesticWorker(propertyId, pass.id, validUntil)
        : renewRecurringPass(propertyId, pass.id, validUntil),
    onSuccess: () => {
      toast.success('Pass renewed');
      void qc.invalidateQueries({ queryKey });
      onOpenChange(false);
    },
    onError: () => {
      toast.error('Action failed. Please try again.');
    },
  });

  const submit = () => {
    if (!value) {
      setError('Pick a new end date');
      return;
    }
    if (pass.validUntil && value <= toDateInput(new Date(pass.validUntil))) {
      setError('Choose a date after the current end date');
      return;
    }
    setError(null);
    // Send an end-of-day ISO timestamp so the pass stays valid through the
    // selected day in the property timezone.
    renew.mutate(new Date(`${value}T23:59:59`).toISOString());
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Renew pass for {pass.visitorName}</DialogTitle>
        </DialogHeader>
        <Field>
          <FieldLabel htmlFor="renew-valid-until">New end date</FieldLabel>
          <Input
            id="renew-valid-until"
            type="date"
            min={minDate}
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
          {error && <FieldError>{error}</FieldError>}
        </Field>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={renew.isPending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={renew.isPending}>
            {renew.isPending ? 'Renewing…' : 'Renew'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Pass columns ──────────────────────────────────────────────────────────

/** Visitor name + status badge + expiring-soon flag — the card header line. */
function NameCell({ pass }: { pass: RecurringPass }) {
  const expiringSoon = isExpiringSoon(pass);
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm font-medium text-[var(--text-primary)]">{pass.visitorName}</span>
      <Badge className={statusBadgeClass(pass.status)}>{pass.status.toUpperCase()}</Badge>
      {expiringSoon && <Badge className="badge-status badge-warning">Expiring soon</Badge>}
    </div>
  );
}

/** End-date copy, amber when the pass is expiring soon (RES-F-09). */
function ExpiryCell({ pass }: { pass: RecurringPass }) {
  const expiringSoon = isExpiringSoon(pass);
  if (!pass.validUntil) return <span className="text-[var(--text-tertiary)]">No end date</span>;
  if (expiringSoon) {
    return (
      <span className="text-[var(--status-amber-text)]">
        Expires {formatDate(pass.validUntil)} · in {daysUntil(pass.validUntil)} day
        {daysUntil(pass.validUntil) === 1 ? '' : 's'}
      </span>
    );
  }
  return <span>Expires {formatDate(pass.validUntil)}</span>;
}

/** Columns shared by both presentations. The visitor/status block and the
 *  schedule are the most scannable fields, so they form the mobile card header
 *  (`primary`); end date and actions collapse into the card body. */
const PASS_COLUMNS: ResponsiveColumn<RecurringPass>[] = [
  {
    key: 'visitor',
    header: 'Visitor',
    priority: 'primary',
    cell: (pass) => <NameCell pass={pass} />,
  },
  {
    key: 'schedule',
    header: 'Schedule',
    priority: 'primary',
    cell: (pass) => (
      <div className="flex flex-wrap items-center gap-2 text-sm text-[var(--text-secondary)]">
        <PassTypeBadge passType={pass.passType} />
        <span>{formatSchedule(pass)}</span>
      </div>
    ),
  },
  {
    key: 'expiry',
    header: 'End date',
    priority: 'secondary',
    className: 'text-xs',
    cell: (pass) => (
      <span className="text-xs text-[var(--text-tertiary)]">
        <ExpiryCell pass={pass} />
      </span>
    ),
  },
];

/** Columns for an interactive section: the shared columns plus a trailing
 *  actions cell. Built per `propertyId` so the actions menu can dispatch the
 *  right mutations. */
function buildPassColumns(propertyId: string): ResponsiveColumn<RecurringPass>[] {
  return [
    ...PASS_COLUMNS,
    {
      key: 'actions',
      header: '',
      priority: 'secondary',
      className: 'w-12 text-right',
      cell: (pass) => <PassActions pass={pass} propertyId={propertyId} />,
    },
  ];
}

// ── Pass row actions ──────────────────────────────────────────────────────

/**
 * The per-row actions menu (Suspend / Reinstate / Renew / Revoke) plus its
 * renew dialog. Extracted from the old `PassCard` so it can live inside a
 * `ResponsiveTable` cell while keeping its own mutation + dialog state. The
 * wrapper stops click propagation so opening the menu doesn't also trigger the
 * row's detail-open handler.
 */
function PassActions({ pass, propertyId }: { pass: RecurringPass; propertyId: string }) {
  const qc = useQueryClient();
  const isDomestic = pass.passType === 'domestic_worker';
  const queryKey = isDomestic ? ['domestic-workers', propertyId] : ['recurring-passes', propertyId];
  const [renewOpen, setRenewOpen] = useState(false);

  // Domestic worker module has no reinstate endpoint — reuse the recurring
  // endpoint for that case; the server's domestic-worker list is a filtered
  // view of the same underlying recurring_passes table.
  const suspend = useMutation({
    mutationFn: () =>
      isDomestic
        ? suspendDomesticWorker(propertyId, pass.id)
        : suspendRecurringPass(propertyId, pass.id),
    onMutate: async () => {
      await qc.cancelQueries({ queryKey });
      const previous = qc.getQueryData<RecurringPass[]>(queryKey);
      qc.setQueryData<RecurringPass[]>(queryKey, (old) =>
        old?.map((p) => (p.id === pass.id ? { ...p, status: 'suspended' as const } : p)),
      );
      return { previous };
    },
    onSuccess: () => {
      toast.success('Pass suspended');
    },
    onError: (_err, _args, context) => {
      if (context?.previous) {
        qc.setQueryData(queryKey, context.previous);
      }
      toast.error('Action failed. Please try again.');
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey });
    },
  });

  const reinstate = useMutation({
    mutationFn: () => reinstateRecurringPass(propertyId, pass.id),
    onMutate: async () => {
      await qc.cancelQueries({ queryKey });
      const previous = qc.getQueryData<RecurringPass[]>(queryKey);
      qc.setQueryData<RecurringPass[]>(queryKey, (old) =>
        old?.map((p) => (p.id === pass.id ? { ...p, status: 'active' as const } : p)),
      );
      return { previous };
    },
    onSuccess: () => {
      toast.success('Pass reinstated');
    },
    onError: (_err, _args, context) => {
      if (context?.previous) {
        qc.setQueryData(queryKey, context.previous);
      }
      toast.error('Action failed. Please try again.');
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey });
    },
  });

  const revoke = useMutation({
    mutationFn: () =>
      isDomestic
        ? revokeDomesticWorker(propertyId, pass.id)
        : revokeRecurringPass(propertyId, pass.id),
    onMutate: async () => {
      await qc.cancelQueries({ queryKey });
      const previous = qc.getQueryData<RecurringPass[]>(queryKey);
      qc.setQueryData<RecurringPass[]>(queryKey, (old) =>
        old?.map((p) => (p.id === pass.id ? { ...p, status: 'revoked' as const } : p)),
      );
      return { previous };
    },
    onSuccess: () => {
      toast.success('Pass revoked');
    },
    onError: (_err, _args, context) => {
      if (context?.previous) {
        qc.setQueryData(queryKey, context.previous);
      }
      toast.error('Action failed. Please try again.');
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey });
    },
  });

  const pending = suspend.isPending || reinstate.isPending || revoke.isPending;
  const isTerminal = pass.status === 'revoked' || pass.status === 'expired';
  // Renew only makes sense for a non-terminal pass that has an end date to
  // extend. The server rejects updates to revoked/expired passes.
  const canRenew = !isTerminal && !!pass.validUntil;

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="sm"
            variant="ghost"
            aria-label="Pass actions"
            disabled={pending || isTerminal}
          >
            <MoreHorizontal className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {pass.status === 'active' && (
            <DropdownMenuItem onSelect={() => suspend.mutate()}>Suspend</DropdownMenuItem>
          )}
          {pass.status === 'suspended' && (
            <DropdownMenuItem onSelect={() => reinstate.mutate()}>Reinstate</DropdownMenuItem>
          )}
          {canRenew && (
            <DropdownMenuItem onSelect={() => setRenewOpen(true)}>Renew…</DropdownMenuItem>
          )}
          <DropdownMenuItem
            onSelect={() => revoke.mutate()}
            className="text-[var(--status-red-text)] focus:text-[var(--status-red-text)]"
          >
            Revoke
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {canRenew && (
        <PassRenewDialog
          open={renewOpen}
          onOpenChange={setRenewOpen}
          pass={pass}
          propertyId={propertyId}
        />
      )}
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────

export function PassesPage() {
  const { isLoading: isAuthLoading } = useAuth();
  const prop = useResidentProperty();
  const [searchParams, setSearchParams] = useSearchParams();
  const [createType, setCreateType] = useState<PassCreateType | null>(null);
  const [selection, setSelection] = useState<PassDetailSelection | null>(null);

  // Auto-open the create dialog when navigated here with ?create=1 (from Home).
  // The ?tab= query param picks which type. Strip both after opening so refresh
  // doesn't re-trigger.
  useEffect(() => {
    if (searchParams.get('create') !== '1') return;
    const tab = searchParams.get('tab');
    setCreateType(tab === 'domestic' ? 'domestic_worker' : 'recurring');
    const next = new URLSearchParams(searchParams);
    next.delete('create');
    next.delete('tab');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const recurringQuery = useQuery({
    queryKey: ['recurring-passes', prop?.propertyId],
    queryFn: () => (prop ? getRecurringPasses(prop.propertyId) : null),
    enabled: !!prop,
  });

  const domesticQuery = useQuery({
    queryKey: ['domestic-workers', prop?.propertyId],
    queryFn: () => (prop ? getDomesticWorkers(prop.propertyId) : null),
    enabled: !!prop,
  });

  const isLoading = recurringQuery.isLoading || domesticQuery.isLoading;
  const isError = recurringQuery.isError || domesticQuery.isError;

  const allPasses = [...(recurringQuery.data ?? []), ...(domesticQuery.data ?? [])].sort((a, b) =>
    a.visitorName.localeCompare(b.visitorName),
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-2xl">Recurring Passes</h1>
        <div className="flex gap-2">
          <button
            onClick={() => setCreateType('recurring')}
            disabled={isAuthLoading || !prop}
            className="flex items-center gap-1.5 rounded-md border border-[var(--bg-surface-raised)] px-3 py-1.5 text-xs font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-raised)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="size-3.5" />
            Recurring Visitor
          </button>
          <button
            onClick={() => setCreateType('domestic_worker')}
            disabled={isAuthLoading || !prop}
            className="flex items-center gap-1.5 rounded-md border border-[var(--bg-surface-raised)] px-3 py-1.5 text-xs font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-raised)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="size-3.5" />
            Domestic Help
          </button>
        </div>
      </div>

      <PassList
        passes={allPasses}
        isLoading={isLoading}
        isError={isError}
        propertyId={prop?.propertyId ?? ''}
        onSelect={(p) =>
          setSelection({
            kind: p.passType === 'domestic_worker' ? 'domestic_worker' : 'recurring',
            pass: p,
          })
        }
      />

      {prop && createType !== null && (
        <PassCreateDialog
          open={true}
          onOpenChange={(open) => {
            if (!open) setCreateType(null);
          }}
          propertyId={prop.propertyId}
          passType={createType}
        />
      )}

      {prop && (
        <PassDetailSheet
          open={!!selection}
          onOpenChange={(next) => {
            if (!next) setSelection(null);
          }}
          propertyId={prop.propertyId}
          selection={selection}
        />
      )}
    </div>
  );
}

// ── Grouped list ────────────────────────────────────────────────────────────

function PassSection({
  title,
  passes,
  propertyId,
  onSelect,
  emptyHint,
}: {
  title: string;
  passes: RecurringPass[];
  propertyId: string;
  onSelect: (pass: RecurringPass) => void;
  emptyHint?: string;
}) {
  return (
    <section aria-label={title}>
      <h2 className="mb-2 text-sm font-semibold text-[var(--text-secondary)]">
        {title} <span className="text-[var(--text-tertiary)]">({passes.length})</span>
      </h2>
      {passes.length === 0 ? (
        emptyHint ? (
          <p className="text-sm text-[var(--text-tertiary)]">{emptyHint}</p>
        ) : null
      ) : (
        <ResponsiveTable
          columns={buildPassColumns(propertyId)}
          rows={passes}
          getRowKey={(p) => p.id}
          onRowClick={onSelect}
          rowLabel={(p) => `View pass for ${p.visitorName}`}
          caption={title}
        />
      )}
    </section>
  );
}

function CollapsibleSection({
  title,
  passes,
  propertyId,
  onSelect,
}: {
  title: string;
  passes: RecurringPass[];
  propertyId: string;
  onSelect: (pass: RecurringPass) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <section aria-label={title}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
      >
        <ChevronDown className={cn('size-4 transition-transform', open && 'rotate-180')} />
        {title} <span className="text-[var(--text-tertiary)]">({passes.length})</span>
      </button>
      {open && (
        <ResponsiveTable
          columns={buildPassColumns(propertyId)}
          rows={passes}
          getRowKey={(p) => p.id}
          onRowClick={onSelect}
          rowLabel={(p) => `View pass for ${p.visitorName}`}
          caption={title}
        />
      )}
    </section>
  );
}

function PassList({
  passes,
  isLoading,
  isError,
  propertyId,
  onSelect,
}: {
  passes: RecurringPass[];
  isLoading: boolean;
  isError: boolean;
  propertyId: string;
  onSelect: (pass: RecurringPass) => void;
}) {
  if (isError) {
    return (
      <div
        role="alert"
        className="rounded-xl border border-[var(--border-error)] bg-[var(--status-red-tint)] p-4"
      >
        <p className="text-sm font-medium text-[var(--text-primary)]">
          Couldn&apos;t load recurring passes
        </p>
        <p className="text-sm text-[var(--text-secondary)]">Please try again later.</p>
      </div>
    );
  }
  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }
  if (passes.length === 0) {
    return (
      <div className="rounded-xl border border-[var(--border-subtle)] py-12 text-center">
        <RefreshCw className="size-10 mx-auto mb-3 text-[var(--text-tertiary)]" />
        <p className="text-sm font-medium text-[var(--text-secondary)]">No recurring passes yet.</p>
        <p className="text-xs text-[var(--text-tertiary)] mt-1">
          Add a pass for a regular visitor — gardener, helper, or anyone who comes weekly.
        </p>
      </div>
    );
  }

  // Group by lifecycle state. Within Active, expiring-soon passes float to the
  // top (RES-F-09); everything else is alphabetical for predictable scanning.
  const byName = (a: RecurringPass, b: RecurringPass) => a.visitorName.localeCompare(b.visitorName);
  const active = passes
    .filter((p) => p.status === 'active')
    .sort((a, b) => Number(isExpiringSoon(b)) - Number(isExpiringSoon(a)) || byName(a, b));
  const suspended = passes.filter((p) => p.status === 'suspended').sort(byName);
  const inactive = passes
    .filter((p) => p.status === 'expired' || p.status === 'revoked')
    .sort(byName);

  return (
    <div className="space-y-6">
      <PassSection
        title="Active"
        passes={active}
        propertyId={propertyId}
        onSelect={onSelect}
        emptyHint="No active passes."
      />
      {suspended.length > 0 && (
        <PassSection
          title="Suspended"
          passes={suspended}
          propertyId={propertyId}
          onSelect={onSelect}
        />
      )}
      {inactive.length > 0 && (
        <CollapsibleSection
          title="Expired / Completed"
          passes={inactive}
          propertyId={propertyId}
          onSelect={onSelect}
        />
      )}
    </div>
  );
}
